import { useTranslation } from 'react-i18next';
import { AutoManagedCommissionStatus, AutoManagedCommissionInfluencer } from '@anytag/graphql';
import { Button, CheckBox, TableCell, TextCutter } from '@anytag/shared-atoms';
import { Badge, BadgeProps } from '@anyui/core';
import { useSelectItemIds } from '@anytag/shared-hooks';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { cn, formatPrice } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';

type SelectCommissionProps = Pick<
  ReturnType<typeof useSelectItemIds<number>>,
  'selectItem' | 'selectedItemIds' | 'hasAllItemsSelected' | 'toggleSelectAllItems'
>;

interface Props extends SelectCommissionProps {
  data: readonly AutoManagedCommissionInfluencer[];
  onApprove: (influencer: { id: number; name: string }) => void;
}

export const useAutoManagedCommissionsInfluencersTable = ({
  data,
  onApprove,
  selectItem,
  selectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems
}: Props) => {
  const { t } = useTranslation();

  const getStatusColumnProps = (status: AutoManagedCommissionStatus): BadgeProps => {
    switch (status) {
      case AutoManagedCommissionStatus.APPROVED:
        return { children: t('Option.Approved'), color: 'green' };
      case AutoManagedCommissionStatus.UNAPPROVED:
        return { children: t('Option.Not Approved'), color: 'zinc' };
      default:
        return { children: null, color: 'zinc' };
    }
  };

  const hasAllApproved = data.every((infl) => infl.status === AutoManagedCommissionStatus.APPROVED);
  const columns: ColumnDef<AutoManagedCommissionInfluencer>[] = [
    {
      accessorKey: 'fixedDate',
      header: () => (
        <div className="flex items-center gap-4">
          {!hasAllApproved ? (
            <CheckBox
              disabled={hasAllApproved}
              onClick={!hasAllApproved ? toggleSelectAllItems : undefined}
              help={t(hasAllItemsSelected ? 'Unselect all' : 'Select all')}
            />
          ) : null}
          <span>{t('HeaderColumn.Fixed date of commission')}</span>
        </div>
      ),
      cell: ({ row }) => {
        const { influencerId, fixedDate, startCoverageDate, endCoverageDate, status } = row.original;
        const isSelected = selectedItemIds.includes(influencerId);
        const isUnapproved = status === AutoManagedCommissionStatus.UNAPPROVED;

        return (
          <div className="flex items-center gap-4">
            {isUnapproved ? (
              <CheckBox
                checked={isSelected}
                className="gap-4"
                key={`checkbox-${influencerId}`}
                onClick={() => selectItem(influencerId)}
                help={isSelected ? t('Unselect') : t('Select')}
              />
            ) : null}
            <TableCell.Text
              // BE gives us `yyyy-MM-DD` already
              value={
                <div className={cn({ 'ml-8': !isUnapproved && !hasAllApproved })}>
                  <span>{fixedDate}</span>&nbsp;
                  <span className="text-[11px] text-tertiary">{`(${startCoverageDate} - ${endCoverageDate})`}</span>
                </div>
              }
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencerId, avatar, name } = row.original;

        return (
          <div className="flex min-w-50 items-center gap-2">
            <InfluencerAvatar size="48px" src={avatar} className="bg-black-20" id={influencerId} />
            <TextCutter lines={3} text={name} />
          </div>
        );
      }
    },
    {
      accessorKey: 'payout',
      header: t<string>('HeaderColumn.Payout'),
      cell: ({ row }) => (
        <TableCell.Text
          className="font-semibold"
          value={`${formatPrice(row.original.revenue, row.original.currency)} ${row.original.currency}`}
        />
      )
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <Badge {...getStatusColumnProps(row.original.status)} />
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.status === AutoManagedCommissionStatus.UNAPPROVED ? (
          <Button
            className="p-4"
            title={t('Button.Approve')}
            onClick={() => onApprove({ id: row.original.influencerId, name: row.original.name })}
          />
        ) : null
    }
  ];

  return { columns };
};
